import React from "react";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { CircularProgress, List, ListSubheader, useTheme } from "@mui/material";

import { useNavigatorContext } from "../context/useNavigatorContext";
import { GROUP_LABELS, NavigationGroup } from "../types";

interface NavigatorGroupProps {
  params: {
    key: number;
    group: string;
    children?: React.ReactNode;
  };
}

export const NavigatorGroup = ({ params }: NavigatorGroupProps) => {
  const theme = useTheme();
  const { loadingGroup, showLoadMore, onLoadMore } = useNavigatorContext();

  const groupKey = params.group as NavigationGroup | undefined;
  const groupLabel = groupKey ? GROUP_LABELS[groupKey] : undefined;

  const isStandalone = !params.group;

  return (
    <List
      sx={{ pb: 1 }}
      className={isStandalone ? "standalone-group" : undefined}
    >
      {!isStandalone && (
        <ListSubheader
          sx={{
            backgroundColor:
              theme.igBackgroundColors.interactive.neutral.active,
            height: 36,
            display: "flex",
            alignItems: "center",
            px: 2,
            top: -8,
            lineHeight: "unset",
          }}
        >
          <IgTypography variant="body2" fontWeight={600}>
            {groupLabel}
          </IgTypography>
        </ListSubheader>
      )}
      {params.children}
      {groupKey && showLoadMore(groupKey) && (
        <LoadMoreButton
          loading={loadingGroup === groupKey}
          disabled={!!loadingGroup}
          onClick={() => onLoadMore(groupKey)}
        />
      )}
    </List>
  );
};

interface LoadMoreButtonProps {
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}

const LoadMoreButton = ({
  loading,
  disabled,
  onClick,
}: LoadMoreButtonProps) => {
  const theme = useTheme();
  return (
    <IgButton
      data-load-more
      disabled={disabled}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        width: "100%",
        py: 1,
        backgroundColor: "transparent",
        color: theme.palette.accent.main,
        "&:hover": {
          backgroundColor: theme.igBackgroundColors.accent,
        },
        "&.Mui-focused, &[aria-selected]": {
          backgroundColor: "transparent",
        },
      }}
    >
      {loading ? (
        <CircularProgress size={16} />
      ) : (
        <IgTypography variant="body2" sx={{ color: "inherit" }}>
          Load more &nbsp;+
        </IgTypography>
      )}
    </IgButton>
  );
};
